import React, { useEffect, useState } from "react";
import { Button, Card, Flex, Typography } from "antd";
import {
    EyeOutlined,
    ShoppingCartOutlined,
    UserOutlined,
    SafetyOutlined,
    MailOutlined,
} from "@ant-design/icons";
import { Column } from "@ant-design/plots";
import { Line } from "@ant-design/plots";
import {
    collection,
    getCountFromServer,
    getDocs,
    query,
    where,
} from "firebase/firestore";
import dayjs from "dayjs";
import { db_fire } from "../firebase";

const { Title } = Typography;

const Home = () => {
    /* ================= STATES ================= */
    const [stats, setStats] = useState({
        orders: 0,
        users: 0,
        delivery: 0,
        admins: 0,
        contacts: 0,
    });

    const [OrdersGraph, setOrdersGraph] = useState([]);
    const [UsersGraph, setUsersGraph] = useState([]);

    const [visitGraph, setVisitGraph] = useState([]);
    const [totalVisits, setTotalVisits] = useState(0);
    const [uniqueVisits, setUniqueVisits] = useState(0);
    const [orderUserGraph, setOrderUserGraph] = useState([]);

    const visitColumnData = visitGraph.flatMap(item => [
        {
            month: item.month,
            type: "Unique Visitors",
            value: item.uniqueUsers,
        },
        {
            month: item.month,
            type: "Total Visits",
            value: item.totalUsers,
        },
    ]);

    /* ================= COUNTS ================= */
    const fetchCounts = async () => {
        const ordersSnap = await getCountFromServer(
            collection(db_fire, "orders")
        );

        const usersSnap = await getCountFromServer(
            query(
                collection(db_fire, "users"), 
            )
        );


        const adminsSnap = await getCountFromServer(
            query(collection(db_fire, "users"), where("admin", "==", true))
        );
        const deliverySnap = await getCountFromServer(
            query(collection(db_fire, "users"), where("role", "==", "Delivery"))
        );

        const contactsSnap = await getCountFromServer(
            collection(db_fire, "contacts")
        );

        setStats({
            orders: ordersSnap.data().count,
            users: usersSnap.data().count,
            delivery: deliverySnap.data().count,
            admins: adminsSnap.data().count,
            contacts: contactsSnap.data().count,
        });

        setOrderUserGraph([
            { type: "Users", value: usersSnap.data().count },
            { type: "Orders", value: ordersSnap.data().count },
        ]);
    };

    const fetchOrders = async () => {
        const snap = await getDocs(collection(db_fire, "orders"));
        const dayMap = {};

        snap.forEach(doc => {
            const date = doc.data().createdAt?.toDate();
            if (!date) return;

            const day = dayjs(date).format("DD MMM");

            dayMap[day] = (dayMap[day] || 0) + 1;
        });

        const graphData = Object.keys(dayMap) 
            .map(day => ({
                date: day,
                value: dayMap[day],
            }));

        setOrdersGraph(graphData);
    };


    /* ================= VISITS TABLE ================= */
    const fetchVisitsTable = async () => {
        const snap = await getDocs(collection(db_fire, "visits"));

        let total = 0;
        let unique = 0;

        const graph = snap.docs
            .map(doc => {
                const d = doc.data();

                total += d.totalVisits || 0;
                unique += d.uniqueVisitors || 0;

                return {
                    month: doc.id, // 01-2026
                    uniqueUsers: d.uniqueVisitors,
                    totalUsers: d.totalVisits,
                };
            })
            .sort(
                (a, b) =>
                    dayjs(a.month, "MM-YYYY").unix() -
                    dayjs(b.month, "MM-YYYY").unix()
            );

        setVisitGraph(graph);
        setTotalVisits(total);
        setUniqueVisits(unique);
    };

    const fetchUsers = async () => {
        const snap = await getDocs(collection(db_fire, "users"));
        const dayMap = {};

        snap 
        .forEach(doc => {
            const date = doc.data().createdAt?.toDate();
            if (!date) return;

            const day = dayjs(date).format("DD MMM");

            dayMap[day] = (dayMap[day] || 0) + 1;
        });

        const graphData = Object.keys(dayMap)
            .map(day => ({
                date: day,
                value: dayMap[day],
            }));

        setUsersGraph(graphData);
    };


    /* ================= INIT ================= */
    useEffect(() => {
        fetchCounts();
        fetchVisitsTable();
        fetchOrders();
        fetchUsers();
    }, []);

    return (
        <> 
        
            {/* ================= SUMMARY ================= */}
            <Flex gap={12} wrap="wrap">
                <Button type="primary" icon={<EyeOutlined />}>
                    {totalVisits} Total Visits
                </Button>

                <Button icon={<EyeOutlined />}>
                    {uniqueVisits} Unique Visitors
                </Button>

                <Button icon={<ShoppingCartOutlined />} href="/order">
                    {stats.orders} Orders
                </Button>

                <Button icon={<UserOutlined />}>
                    {stats.users} Users
                </Button>

                <Button icon={<UserOutlined />}>
                    {stats.delivery} Delivery Partner
                </Button>

                <Button icon={<SafetyOutlined />}>
                    {stats.admins} Admins
                </Button>

                <Button icon={<MailOutlined />}>
                    {stats.contacts} Contacts
                </Button>
            </Flex>

            {/* ================= GRAPHS ================= */}
            <Flex vertical gap={20} style={{ marginTop: 24 }}>
                <Card title="Monthly Visitors (Unique vs Total)">
                    <Column
                        data={visitColumnData}
                        xField="month"
                        yField="value"
                        seriesField="type"
                        isGroup
                        colorField="type"
                        color={({ type }) =>
                            type === "Unique Visitors" ? "#3474c7" : "#fa8c16"
                        }
                        label={{
                            style: {
                                fill: "#fff",
                                fontSize: 12,
                                fontWeight: 600,
                                textAlign: "center",
                            },
                            content: (item) => `${item.value}\n${item.type}`,
                        }}
                        autoFit
                        legend={{ position: "top" }}
                    />
                </Card>

                <Card title=" Orders">
                    <Line
                        data={OrdersGraph}
                        xField="date"
                        yField="value"
                        smooth
                        color="#fa541c"   // red
                        point={{ size: 4 }}
                        autoFit
                    />
                </Card>

                <Card title=" Users">
                    <Line
                        data={UsersGraph}
                        xField="date"
                        yField="value"
                        smooth
                        color="#52c41a"   // green
                        point={{ size: 4 }}
                        autoFit
                    />
                </Card>

            </Flex>
        </>
    );
};

export default Home;
